import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { EvtHeader, EvtSectionHeader } from "components/header";
import { MilestoneBurnChart } from "components/job/burn-chart";
import EditItemButton from "components/buttons/edit";
import { EvtCoordinatorCard } from "components/coordinator/display-card";
import { MilestoneTotalConsumedHoursStat } from "components/milestone/stats/consumed/stat";
import { MilestoneTotalSpentHoursStat } from "components/milestone/stats/spent/stat";
import { MilestoneTotalApprovedHoursStat } from "components/milestone/stats/approved/stat";
import { MilestoneTotalLoggedHoursStat } from "components/milestone/stats/logged/stat";
import { MilestoneBillableHoursStat } from "components/milestone/stats/billable/stat";
import { UserFieldInput } from "permissions/components/user-field-input";
import { PermissionControlledInput } from "permissions/components/input";
import HeaderPortal from "components/portal/header-portal";
import { StatusDisplay } from "@/components/status/display";
import { useMilestone } from "@/modules/work/milestones/context";
import MilestoneEmployeeTimesheet from "@/modules/work/milestones/components/employee-timesheet";
import { fetchMilestone } from "@/modules/work/milestones/api";
import { DashboardInfoField } from "components/dashboard/info-field";
import { DisplayDate } from "components/display/date";

export default function MilestoneDashboard() {
  const { milestoneId } = useParams();
  const { jobType } = useMilestone();

  const { data: milestone, isLoading, isError } = useQuery({
    queryFn: () => fetchMilestone({ milestoneId }),
    queryKey: ["milestone", milestoneId],
    enabled: !!milestoneId,
  });

  if (isLoading) return <div className="p-8">Loading...</div>;
  if (isError)
    return (
      <div className="p-8 text-red-500">Error loading milestone details.</div>
    );
  if (!milestone) return <div className="p-8">No milestone found.</div>;

  return (
    <div className="flex-1 pb-5">
      <EvtHeader title={milestone.name} description={<StatusDisplay value={milestone.status} />} />
      <section>
        <div className="grid grid-cols-4 px-4 gap-4 md:px-6 mt-2.5">
          <DashboardInfoField label="Start Date">
            <DisplayDate value={milestone.start_date} />
          </DashboardInfoField>
          <DashboardInfoField label="End Date">
            <DisplayDate value={milestone.end_date} />
          </DashboardInfoField>
        </div>
      </section>
      <section className="mt-3.5">
        <EvtSectionHeader title="Coordinator" />
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 px-4 gap-4 md:px-6 mt-2.5">
          <EvtCoordinatorCard
            collection="milestones"
            id={milestoneId}
            role="Milestone Coordinator"
          />
        </div>
      </section>

      <section className="mt-3.5">
        <EvtSectionHeader title="Budget" />
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 px-4 gap-4 md:px-6 mt-2.5">
          {["fixed", "dedicated"].includes(jobType) && (
            <>
              <MilestoneTotalConsumedHoursStat id={milestoneId} />
              <MilestoneTotalSpentHoursStat id={milestoneId} />
            </>
          )}
          <MilestoneTotalApprovedHoursStat id={milestoneId} />
          <MilestoneTotalLoggedHoursStat id={milestoneId} />
          {(jobType === "hourly" || jobType === "bucket") && (
            <UserFieldInput targetItem={milestoneId} field="billable_hours" collection="milestones">
              <MilestoneBillableHoursStat id={milestoneId} />
            </UserFieldInput>
          )}
        </div>
      </section>

      <section className="mt-3.5">
        <EvtSectionHeader title="Burned Chart" />
        <div className="burnchart px-6 py-2">
          <MilestoneBurnChart id={milestoneId} />
        </div>
      </section>
      <section className="mt-3.5">
        <div className="mt-3.5">
          <MilestoneEmployeeTimesheet />
        </div>
      </section>
      <HeaderPortal>
        <PermissionControlledInput
          rule="update:PC,SC,JC,MC,AM"
          collection="milestones"
        >
          <EditItemButton variant="ghost" />
        </PermissionControlledInput>
      </HeaderPortal>
    </div>
  );
}
